import React from 'react'
import { Chart } from 'react-chartjs-2'

import { useDatasetStyleProps, IChartJsDatasetStyles } from 'src/commons/chartjs'

import { IGenericBucket } from '../types'
import { getUniqueCategories } from '../utils'

import { buildHistogramOptions, getChartPlugins, IGraphProps, STYLE_CHART_CONTAINER } from './graphCommons'
import { useGraphContext } from './GraphWrapper/GraphWrappers'
import { DEFAULT_COLORS } from './themes'

const getDocCount = (bucket: IGenericBucket): number => bucket.doc_count || 0

function getData(
  buckets: IGenericBucket[],
  datasetStylesMapping: Record<string, IChartJsDatasetStyles>,
  formatLabel: (x: any) => string = (x: any) => x
) {
  const labels: string | string[] = buckets.map((bucket: IGenericBucket) => bucket.key as string)

  const stackedBuckets = buckets.map((b: any): any => b.stackedBy && b.stackedBy.buckets).filter(Boolean)
  const uniqueCategories = getUniqueCategories(stackedBuckets)

  const datasets: any[] = uniqueCategories.map((category: string) => {
    const datasetStyles = datasetStylesMapping[category]
    return {
      ...datasetStyles,
      label: formatLabel(category),
      baseLabel: category,
      data: stackedBuckets.map((bucket: any[]): number =>
        getDocCount(bucket.find((b: IGenericBucket) => b.key === category) || {})
      ),
    }
  })

  return {
    labels,
    datasets,
  }
}

interface IProps extends IGraphProps {
  enableAnimation?: boolean
  formatLabel?: (x: any) => string
  horizontal?: boolean
}

function Histogram({
  title = '',
  showTitle,
  w = 600,
  h = 300,
  rawData,
  formatWithUnit,
  horizontal,
  maxValue = 0,
  enableAnimation = true,
  xTicksFormatter,
  datasetCategory,
  formatLabel,
}: IProps) {
  const { reactChart } = useGraphContext()

  const datasetStylesMapping = useDatasetStyleProps(datasetCategory, DEFAULT_COLORS)
  const data = getData(rawData, datasetStylesMapping, formatLabel)
  // We show the legend if there are more than 1 datasets to show on the graph
  const displayLegend = data.datasets.length > 1
  const options = buildHistogramOptions({
    title,
    showTitle,
    maxValue,
    enableAnimation,
    formatWithUnit,
    horizontal,
    displayLegend,
    xTicksFormatter,
    adjacentBars: false,
  })

  const plugins = getChartPlugins()

  return (
    <div style={STYLE_CHART_CONTAINER}>
      <Chart
        title={title}
        ref={reactChart.chartRef}
        type="bar"
        data={data}
        width={w}
        height={h}
        options={options}
        plugins={plugins}
      />
    </div>
  )
}

export default Histogram
